import React, { useEffect, useRef } from "react";
import moment from "moment";
import { goBack } from "connected-react-router";

import propTypes from "../../../../constants/propTypes";
import {
  getTimezoneFromBidGroup,
  getTimezoneFromScope,
} from "../../../../utils/timezoneUtils";
import useActions from "../../../hooks/useActions";
import _get from "lodash/get";
import {
  fetchBidGroupVendor,
  vendorQuoteDataComponentId,
} from "../../../../actions/vendorPortalRFQActions";
import { useV2DatacomponentResources } from "../../../hooks/useResources";
import * as RequestTypes from "../../../../constants/RequestTypes";
import QuoteForm from "./QuoteForm";
import { LabeledTextContext } from "../../../../withPORevision";
import Loader, { LoaderContext } from "../../../ui/Loader";
import NoAccessState from "../../../ui/NoAccessState";
import EmptyState from "../../../ui/EmptyState";
import ErrorOutline from "@material-ui/icons/ErrorOutline";
import {
  useDatacomponent,
  useFlattenedDatacomponent,
} from "../../../hooks/useDatacomponent";
import ProjectHeader from "../../Projects/ProjectHeader";
import { useUpdateProcessRequests } from "../../../hooks/useProcessRequest";
import { handleRequestError } from "../../../../utils/formValidationUtils";
import {
  setAutoSaveComponentId,
  showSnackNotificationAction,
} from "../../../../actions/layoutActions";
import { SubmittedBanner } from "./SubmittedBanner";
import * as REQUEST_TYPES from "../../../../constants/RequestTypes";

// 403 (explicitly forbidden) and 404 (record hidden from a contact who lost
// access) both mean "you can't see this RFQ" for a vendor contact.
const NO_ACCESS_STATUSES = [403, 404];

// Maps the FIND request's raw flags to the page's terminal view state. Kept as
// a standalone helper so the branching lives in one small, testable place.
function getFindViewState(findRequest, hasBidGroup) {
  const { loading: requestLoading, success, error } = findRequest;
  const status = error ? error.status : null;
  const noAccess = NO_ACCESS_STATUSES.includes(status);
  // `settled` (request resolved to success or error) keeps the initial
  // pre-dispatch render on the loader instead of flashing an error.
  const settled = success || Boolean(error);

  return {
    loading: requestLoading || !settled,
    noAccess,
    genericError: settled && !noAccess && !hasBidGroup,
  };
}

function useQuoteDetailData(bidGroupVendorId, actions) {
  const draftRef = useRef(true);
  const formikActionsRef = useRef(null);

  useEffect(() => {
    actions.fetchBidGroupVendor(bidGroupVendorId);
    actions.setAutoSaveComponentId(vendorQuoteDataComponentId);
  }, [actions, bidGroupVendorId]);

  const bidGroupVendor = useV2DatacomponentResources(
    vendorQuoteDataComponentId,
    {},
    RequestTypes.FIND
  );

  const dataComponent = useDatacomponent(vendorQuoteDataComponentId);

  useUpdateProcessRequests(dataComponent, {
    onSuccess: () => {
      if (!draftRef.current) {
        actions.goBack();
      }
      actions.showSnackNotificationAction(
        "Your quote have been updated successfully"
      );
    },
    onError: error => handleRequestError(error, formikActionsRef.current),
  });

  const { loading, noAccess, genericError } = getFindViewState(
    useFlattenedDatacomponent(vendorQuoteDataComponentId, REQUEST_TYPES.FIND),
    Boolean(bidGroupVendor.bidGroup)
  );

  return {
    bidGroupVendor,
    loading,
    noAccess,
    genericError,
    draftRef,
    formikActionsRef,
  };
}

function getTimezoneWithFallbacks(bidGroupVendor) {
  let scopeName = _get(
    bidGroupVendor.bidGroup,
    "project.property.entity.client.scope.name",
    _get(bidGroupVendor.vendor, "scope.name", null)
  );

  if (bidGroupVendor.bidGroup && !scopeName) {
    return getTimezoneFromBidGroup(bidGroupVendor.bidGroup);
  }

  return getTimezoneFromScope(scopeName);
}

function getQuoteMeta(bidGroupVendor) {
  const responseDate = bidGroupVendor?.bidGroup?.rfq?.responseDate;

  const quoteIsOver = responseDate
    ? moment(responseDate).diff(moment(), "days") < 0
    : false;

  const projectDefaultCurrency = bidGroupVendor.bidGroup?.project?.projectCurrencies?.find(
    pcItem => pcItem.isDefault
  );

  const timezone = getTimezoneWithFallbacks(bidGroupVendor);

  const labeledText = quoteIsOver || bidGroupVendor.bidComplete;

  return {
    quoteIsOver,
    projectDefaultCurrency,
    timezone,
    labeledText,
  };
}

function QuoteDetail({ match }) {
  const bidGroupVendorId = match.params.bidGroupVendorId;

  const actions = useActions({
    fetchBidGroupVendor,
    goBack,
    showSnackNotificationAction,
    setAutoSaveComponentId,
  });

  const {
    bidGroupVendor,
    loading,
    noAccess,
    genericError,
    draftRef,
    formikActionsRef,
  } = useQuoteDetailData(bidGroupVendorId, actions);

  const {
    quoteIsOver,
    projectDefaultCurrency,
    timezone,
    labeledText,
  } = getQuoteMeta(bidGroupVendor);

  if (noAccess) {
    return <NoAccessState />;
  }

  if (genericError) {
    return (
      <EmptyState icon={ErrorOutline}>
        Something went wrong while loading this RFQ. Please try again later.
      </EmptyState>
    );
  }

  return (
    <LabeledTextContext.Provider value={labeledText}>
      <LoaderContext.Provider value={{ loading }}>
        <SubmittedBanner
          quoteStatus={bidGroupVendor.quoteStatus}
          projectLead={bidGroupVendor.bidGroup?.project.projectLead}
          loading={loading}
        />

        <ProjectHeader project={bidGroupVendor.bidGroup?.project} />

        <Loader>
          <QuoteForm
            quoteIsOver={quoteIsOver}
            bidGroupVendor={bidGroupVendor}
            projectDefaultCurrency={projectDefaultCurrency}
            timezone={timezone}
            draftRef={draftRef}
            formikActionsRef={formikActionsRef}
          />
        </Loader>
      </LoaderContext.Provider>
    </LabeledTextContext.Provider>
  );
}

QuoteDetail.propTypes = {
  match: propTypes.matchRouter,
};

export default QuoteDetail;
